import React, { useState } from 'react';
import { authService, createDoctorProfile } from '../../services/api';
import Button from '../common/Button';

const AddDoctorModal = ({ clinicId, isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState(1); // 1: User signup, 2: Doctor profile
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Step 1: User signup data
    const [userData, setUserData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        role: 'DOCTOR'
    });
    
    // Step 2: Doctor profile data
    const [doctorData, setDoctorData] = useState({
        specialization: '',
        experienceYears: '',
        profileImageUrl: '',
        bio: '',
        qualifications: [],
        achievements: [],
        certifications: []
    });
    
    const [userId, setUserId] = useState(null);

    // Validation functions
    const validateEmail = (email) => {
        if (!email) return true; // Email is optional
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return true; // Phone number is optional
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phoneNumber);
    };

    const validateDateOfBirth = (dateOfBirth) => {
        if (!dateOfBirth) return false; // Date of birth is required
        
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        // Check if birthday has occurred this year
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            return age - 1 >= 18;
        }
        
        return age >= 18;
    };

    const validateUserData = () => {
        // Required fields
        if (!userData.firstName || !userData.lastName || !userData.dateOfBirth) {
            setError('First name, last name, and date of birth are required');
            return false;
        }
        
        // At least one contact method required
        if (!userData.email && !userData.phoneNumber) {
            setError('Either email or phone number is required');
            return false;
        }

        // Email validation
        if (userData.email && !validateEmail(userData.email)) {
            setError('Please enter a valid email address');
            return false;
        }

        // Phone number validation
        if (userData.phoneNumber && !validatePhoneNumber(userData.phoneNumber)) {
            setError('Phone number must be exactly 10 digits');
            return false;
        }

        // Date of birth validation
        if (!validateDateOfBirth(userData.dateOfBirth)) {
            setError('Date of birth is required and person must be at least 18 years old');
            return false;
        }

        return true;
    };

    const handleUserDataChange = (field, value) => {
        setUserData(prev => ({ ...prev, [field]: value }));
    };

    const handleDoctorDataChange = (field, value) => {
        setDoctorData(prev => ({ ...prev, [field]: value }));
    };

    const handleUserSignup = async (e) => {
        e.preventDefault();
        
        // Clear previous errors
        setError(null);
        
        // Validate user data
        if (!validateUserData()) {
            return;
        }

        setLoading(true);

        try {
            const response = await authService.signup(userData);
            if (response.success && response.data?.user_onboard_response?.userId) {
                setUserId(response.data.user_onboard_response.userId);
                setStep(2);
            } else {
                setError(response.message || 'Failed to create user account');
            }
        } catch (err) {
            setError('Failed to create user account. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDoctorProfileCreation = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!doctorData.specialization || !doctorData.experienceYears) {
            setError('Specialization and experience years are required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const profileData = {
                doctorUserId: userId,
                clinicId: clinicId,
                specialization: doctorData.specialization,
                experienceYears: parseInt(doctorData.experienceYears),
                profileImageUrl: doctorData.profileImageUrl || '',
                bio: doctorData.bio || '',
                qualifications: doctorData.qualifications,
                achievements: doctorData.achievements,
                certifications: doctorData.certifications
            };

            const response = await createDoctorProfile(profileData);
            if (response.success) {
                onSuccess && onSuccess();
                onClose();
            } else {
                setError(response.message || 'Failed to create doctor profile');
            }
        } catch (err) {
            setError('Failed to create doctor profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setUserData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            dateOfBirth: '',
            role: 'DOCTOR'
        });
        setDoctorData({
            specialization: '',
            experienceYears: '',
            profileImageUrl: '',
            bio: '',
            qualifications: [],
            achievements: [],
            certifications: []
        });
        setUserId(null);
        setError(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                    <div className="flex justify-between items-center mb-4 sm:mb-6">
                        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                            Add New Doctor
                        </h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Progress Steps */}
                    <div className="flex items-center mb-4 sm:mb-6">
                        <div className={`flex items-center ${step >= 1 ? 'text-purple-600' : 'text-gray-400'}`}>
                            <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 text-sm sm:text-base ${
                                step >= 1 ? 'border-purple-600 bg-purple-600 text-white' : 'border-gray-300'
                            }`}>
                                1
                            </div>
                            <span className="ml-2 text-sm sm:text-base">User Account</span>
                        </div>
                        <div className={`flex-1 h-1 mx-2 sm:mx-4 ${step >= 2 ? 'bg-purple-600' : 'bg-gray-300'}`}></div>
                        <div className={`flex items-center ${step >= 2 ? 'text-purple-600' : 'text-gray-400'}`}>
                            <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 text-sm sm:text-base ${
                                step >= 2 ? 'border-purple-600 bg-purple-600 text-white' : 'border-gray-300'
                            }`}>
                                2
                            </div>
                            <span className="ml-2 text-sm sm:text-base">Doctor Profile</span>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-3 rounded-md mb-4">
                            {error}
                        </div>
                    )}

                    {/* Step 1: User Signup */}
                    {step === 1 && (
                        <form onSubmit={handleUserSignup} className="space-y-4">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Step 1: Create User Account</h3>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        First Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={userData.firstName}
                                        onChange={(e) => handleUserDataChange('firstName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Last Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={userData.lastName}
                                        onChange={(e) => handleUserDataChange('lastName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={userData.email}
                                        onChange={(e) => handleUserDataChange('email', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                            userData.email && !validateEmail(userData.email) 
                                                ? 'border-red-300 focus:ring-red-500' 
                                                : 'border-gray-300'
                                        }`}
                                        placeholder="Enter valid email address"
                                    />
                                    {userData.email && !validateEmail(userData.email) && (
                                        <p className="text-red-500 text-sm mt-1">Please enter a valid email address</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={userData.phoneNumber}
                                        onChange={(e) => handleUserDataChange('phoneNumber', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                            userData.phoneNumber && !validatePhoneNumber(userData.phoneNumber) 
                                                ? 'border-red-300 focus:ring-red-500' 
                                                : 'border-gray-300'
                                        }`}
                                        placeholder="10 digits only"
                                        maxLength={10}
                                    />
                                    {userData.phoneNumber && !validatePhoneNumber(userData.phoneNumber) && (
                                        <p className="text-red-500 text-sm mt-1">Phone number must be exactly 10 digits</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date of Birth *
                                </label>
                                <input
                                    type="date"
                                    value={userData.dateOfBirth}
                                    onChange={(e) => handleUserDataChange('dateOfBirth', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                        userData.dateOfBirth && !validateDateOfBirth(userData.dateOfBirth) 
                                            ? 'border-red-300 focus:ring-red-500' 
                                            : 'border-gray-300'
                                    }`}
                                    required
                                />
                                {userData.dateOfBirth && !validateDateOfBirth(userData.dateOfBirth) && (
                                    <p className="text-red-500 text-sm mt-1">Person must be at least 18 years old</p>
                                )}
                            </div>

                            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleClose}
                                    loading={loading}
                                    className="w-full sm:w-auto"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    loading={loading}
                                    className="w-full sm:w-auto"
                                >
                                    Next: Create Profile
                                </Button>
                            </div>
                        </form>
                    )}

                    {/* Step 2: Doctor Profile */}
                    {step === 2 && (
                        <form onSubmit={handleDoctorProfileCreation} className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 2: Create Doctor Profile</h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Specialization *
                                    </label>
                                    <input
                                        type="text"
                                        value={doctorData.specialization}
                                        onChange={(e) => handleDoctorDataChange('specialization', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Experience (Years) *
                                    </label>
                                    <input
                                        type="number"
                                        value={doctorData.experienceYears}
                                        onChange={(e) => handleDoctorDataChange('experienceYears', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Bio
                                </label>
                                <textarea
                                    value={doctorData.bio}
                                    onChange={(e) => handleDoctorDataChange('bio', e.target.value)}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => setStep(1)}
                                    loading={loading}
                                    className="w-full sm:w-auto"
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    loading={loading}
                                    className="w-full sm:w-auto"
                                >
                                    Create Doctor
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddDoctorModal; 